import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LanguageContext = createContext();

// Authenticated axios instance using httpOnly cookies
const authAxios = axios.create({ baseURL: API_URL, withCredentials: true });

// Supported languages configuration
const SUPPORTED_LANGUAGES = ['en', 'ar', 'fr', 'de', 'es'];
const RTL_LANGUAGES = ['ar'];

export const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' }
];

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState('en');
  const [isRTL, setIsRTL] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Normalize language code to supported language
  const normalizeLanguage = (lang) => {
    if (!lang) return 'en';
    const baseLang = lang.split('-')[0].toLowerCase();
    return SUPPORTED_LANGUAGES.includes(baseLang) ? baseLang : 'en';
  };

  // Detect browser language for first-time visitors
  const detectBrowserLanguage = () => {
    const browserLang = navigator.language || navigator.userLanguage;
    if (!browserLang) return 'en';
    const baseLang = browserLang.split('-')[0].toLowerCase();
    return SUPPORTED_LANGUAGES.includes(baseLang) ? baseLang : 'en';
  };

  // Load user preference on mount
  useEffect(() => {
    const loadPreference = async () => {
      try {
        const savedLang = sessionStorage.getItem('i18nextLng');
        const isFirstVisit = !savedLang;
        let initialLang = isFirstVisit ? detectBrowserLanguage() : normalizeLanguage(savedLang);

        // Try to get from user profile via httpOnly cookie auth
        try {
          const response = await authAxios.get('/api/auth/me');
          if (response.data.language_preference) {
            const userLang = normalizeLanguage(response.data.language_preference);
            applyLanguage(userLang);
            setIsLoading(false);
            return;
          }
        } catch {
          // Not logged in or error — use detected/saved language
        }

        applyLanguage(initialLang);
      } catch (err) {
        console.error('Failed to load language preference:', err);
        applyLanguage('en');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyLanguage = (lang) => {
    const normalizedLang = normalizeLanguage(lang);
    const isRTLLang = RTL_LANGUAGES.includes(normalizedLang);
    
    i18n.changeLanguage(normalizedLang);
    setLanguage(normalizedLang);
    setIsRTL(isRTLLang);
    document.documentElement.dir = isRTLLang ? 'rtl' : 'ltr';
    document.documentElement.lang = normalizedLang;
    localStorage.setItem('i18nextLng', normalizedLang);
    sessionStorage.setItem('i18nextLng', normalizedLang);
  };

  const toggleLanguage = async () => {
    const currentIndex = SUPPORTED_LANGUAGES.indexOf(language);
    const nextIndex = (currentIndex + 1) % SUPPORTED_LANGUAGES.length;
    const newLang = SUPPORTED_LANGUAGES[nextIndex];
    applyLanguage(newLang);
    
    // Persist to database via httpOnly cookie auth
    try {
      await authAxios.put('/api/auth/preferences', { language_preference: newLang });
    } catch {
      // Not logged in — preference saved locally only
    }
  };

  const changeLanguage = async (lang) => {
    const normalizedLang = normalizeLanguage(lang);
    applyLanguage(normalizedLang);
    
    // Persist to database via httpOnly cookie auth
    try {
      await authAxios.put('/api/auth/preferences', { language_preference: normalizedLang });
    } catch {
      // Not logged in — preference saved locally only
    }
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      isRTL, 
      isLoading, 
      toggleLanguage, 
      changeLanguage,
      supportedLanguages: SUPPORTED_LANGUAGES,
      languageOptions: LANGUAGE_OPTIONS
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
